import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ChevronRight, Database, Shuffle } from 'lucide-react';
import { api, TestCatalogResponse } from '../utils/api';
import { useAuthStore } from '../store';

type ExamFamily = 'hsk3' | 'csca';
const CSCA_SUBJECTS: Record<number, string> = { 1: '文科中文', 2: '理科中文', 3: '数学', 4: '物理', 5: '化学' };

const HSK30SystemLevel: React.FC<{ systemType: 'workbook' | 'simulation'; family?: ExamFamily }> = ({ systemType, family = 'hsk3' }) => {
  const { examLevel } = useParams<{ examLevel: string }>();
  const { token } = useAuthStore();
  const level = Number(String(examLevel || '').replace(/\D/g, ''));
  const isCsca = family === 'csca';
  const maxLevel = isCsca ? 5 : 6;
  const safeLevel = level >= 1 && level <= maxLevel ? level : 1;
  const subjectName = CSCA_SUBJECTS[safeLevel] || '文科中文';
  const [catalog, setCatalog] = useState<TestCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    if (isCsca) {
      setCatalog(null);
      setError('');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    api.getTestCatalog(token, { bankVersion: '3.0', systemType, level: safeLevel })
      .then((response) => {
        if (!cancelled) setCatalog(response);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || '题库状态读取失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isCsca, safeLevel, systemType, token]);

  const levelInfo = useMemo(
    () => catalog?.levels.find((item) => item.level === safeLevel),
    [catalog, safeLevel]
  );
  const isWorkbook = systemType === 'workbook';
  const Icon = isWorkbook ? BookOpen : Shuffle;
  const startPath = (part: string) => {
    const params = new URLSearchParams({ bankVersion: '3.0', systemType });
    if (isWorkbook) params.set('workbookPart', part);
    return `/test/${safeLevel}/${isWorkbook ? 'workbook' : encodeURIComponent(part)}?${params.toString()}`;
  };

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-[#4F46E5] hover:text-indigo-700">
        <ArrowLeft className="w-4 h-4" /> 返回 {isCsca ? 'CSCA' : 'HSK 3.0'}
      </Link>

      <section className={`rounded-3xl p-6 md:p-8 border ${isWorkbook ? 'bg-indigo-50 border-indigo-200' : 'bg-orange-50 border-orange-200'}`}>
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isWorkbook ? 'bg-[#4F46E5] text-white' : 'bg-orange-500 text-white'}`}>
            <Icon className="w-7 h-7" />
          </div>
          <div>
            <div className="text-sm font-bold text-gray-500">{isCsca ? `CSCA · level ${safeLevel} · ${subjectName}` : `HSK 3.0 · HSK ${safeLevel}`}</div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#312E81]">{isWorkbook ? 'Workbook' : 'Simulation'}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {isWorkbook ? '固定章节、固定题目、固定顺序' : '独立 Simulation 题库，随机组卷'}
            </p>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="bg-white rounded-3xl p-10 text-center text-gray-500">正在读取题库状态…</div>
      ) : error ? (
        <div className="bg-white rounded-3xl p-10 text-center text-red-600">{error}</div>
      ) : isCsca || (levelInfo?.question_count || 0) === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center border border-gray-100 shadow-sm">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center mx-auto mb-4">
            <Database className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">题库暂未添加</h2>
          <p className="text-gray-500 mt-2">
            {isCsca ? `CSCA · level ${safeLevel} · ${subjectName}` : `HSK 3.0 · HSK ${safeLevel}`} · {isWorkbook ? 'Workbook' : 'Simulation'} 当前为独立空题库。
          </p>
          <p className="text-sm text-gray-400 mt-1">不会自动使用另一套体系的题目。</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold text-[#312E81] mb-4">{isWorkbook ? 'Workbook 章节' : 'Simulation 题型'}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(levelInfo?.parts || []).map((part) => (
              <Link
                key={part.part}
                to={startPath(part.part)}
                className="group rounded-2xl border border-gray-200 p-4 hover:border-indigo-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-bold text-[#312E81]">{part.part}</div>
                    <div className="text-sm text-gray-500 mt-1">{part.question_count} 题</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const CSCASystemLevel: React.FC<{ systemType: 'workbook' | 'simulation' }> = ({ systemType }) => (
  <HSK30SystemLevel systemType={systemType} family="csca" />
);

export default HSK30SystemLevel;
